import { Aspect, CompanyModeNameType } from "@oysterhr/schema"
import { Toast, useToast } from "@oysterhr/ui"
import {
  createContext,
  type PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import useGetCurrentAspect from "./hooks/useGetCurrentAspect"
import useGetNewAspect from "./hooks/useGetNewAspect"
import { useAspects } from "./queries/useAspects"
import { useResellerAspects } from "./queries/useResellerAspects"
import { AccountAspectContextData, AccountAspectProviderProps, AspectTypes } from "./types"
import {
  ASPECT_SEARCH_PARAM,
  FALLBACK_ASPECT,
  STORED_ASPECT,
  STORED_ASPECT_TYPE,
} from "./utils/constants"
import { getUrlAspectId } from "./utils/currentAspect"
import { AccountAspectType } from "./utils/enums"
import { setAspectTypeMap } from "./utils/utils"

export const AccountAspectContext = createContext<AccountAspectContextData | null>(null)

export function useAccountAspect() {
  const contextData = useContext(AccountAspectContext)

  if (!contextData) {
    throw new Error("The provider for AccountAspectContext is missing!")
  }

  return contextData
}

const AccountAspectContextProvider = ({
  availableAspects,
  inferredAspect,
  children,
}: PropsWithChildren<AccountAspectProviderProps>) => {
  const redirectPath = useRef("/")
  const { t } = useTranslation("identity/AspectSwitcher")
  const toast = useToast()
  const [currentAspect, setAccountAspectState] = useState<Aspect>(inferredAspect)
  const [newAspectId, setNewAspectId] = useState("")
  const newAspect = useGetNewAspect({ id: newAspectId, availableAspects })
  const isResellerCompany =
    currentAspect.company?.modeName === CompanyModeNameType.Reseller ||
    currentAspect.company?.modeName === CompanyModeNameType.ResellerEndClient

  const renderChangeAspectToast = useRef(() =>
    toast({
      render: (
        <Toast variation="success">
          <Toast.Content>{t("toast.successMessage")}</Toast.Content>
        </Toast>
      ),
    }),
  )

  const isAspectTypeMap = useMemo<{
    [key in AspectTypes]: boolean
  }>(() => setAspectTypeMap(currentAspect.type as AccountAspectType), [currentAspect])

  useEffect(() => {
    window.localStorage.setItem(STORED_ASPECT, currentAspect.id)
    window.localStorage.setItem(STORED_ASPECT_TYPE, currentAspect.type)
  }, [currentAspect])

  useEffect(() => {
    if (newAspect && newAspect.id !== currentAspect?.id) {
      setAccountAspectState(newAspect)
      window.history.replaceState(null, "", redirectPath.current)
      renderChangeAspectToast.current()
      redirectPath.current = "/"
    }
  }, [newAspect, currentAspect])

  const changeAccountAspect = (newId: string, newRedirectPath?: string) => {
    if (newRedirectPath) redirectPath.current = newRedirectPath

    setNewAspectId(newId)
  }

  return (
    <AccountAspectContext.Provider
      key={currentAspect?.id}
      value={{
        accountAspect: currentAspect.id,
        changeAccountAspect,
        availableAspects,
        aspectType: currentAspect.type as AccountAspectType,
        currentAspect,
        isResellerCompany,
        ...isAspectTypeMap,
      }}
    >
      {children}
    </AccountAspectContext.Provider>
  )
}

const AccountAspectInitialState = ({
  mappedAvailableAspects,
  fallbackAspectId,
  children,
}: {
  mappedAvailableAspects: Record<string, Aspect>
  fallbackAspectId: string
  children: (aspect: Aspect | undefined) => ReactNode
}) => {
  const url = useMemo(() => new URL(window.location.href), [])
  const urlAspectId = useMemo(() => getUrlAspectId(url), [url])
  const currentAspect = useGetCurrentAspect({
    urlAspectId,
    mappedAvailableAspects,
  })

  useEffect(() => {
    if (currentAspect === undefined) {
      // when the aspect cannot be inferred, set the fallback
      window.localStorage.setItem(STORED_ASPECT, fallbackAspectId)

      // if the url contains an invalid aspect, redirect to root
      if (urlAspectId) {
        window.location.href = url.origin
        return
      }
    }

    if (url.searchParams.get(ASPECT_SEARCH_PARAM)) {
      // when an aspect search param exists, remove it and reload
      url.searchParams.delete(ASPECT_SEARCH_PARAM)
      window.history.replaceState(null, "", url?.href || "/")
    }
  }, [currentAspect, url, fallbackAspectId, urlAspectId])

  if (currentAspect === false) return null

  return <>{children(currentAspect)}</>
}

export const AccountAspectContextWrapper = ({
  skipFetchAspects,
  ...props
}: PropsWithChildren<{ skipFetchAspects?: boolean }>) => {
  const { data, loading, error } = useAspects({ skip: skipFetchAspects })
  const { data: resellerData, loading: loadingReseller } = useResellerAspects({
    variables: { perPage: 1 },

    skip: !!error || skipFetchAspects,
  })
  const storedAspectId = useMemo(() => window.localStorage.getItem(STORED_ASPECT) || "", [])
  const availableAspects: Aspect[] = useMemo(() => data?.aspects || [], [data])

  // This maps the available aspects to an object like:
  // {
  //    "Company:12435: { id: "Company:12435", ...},
  //    "Employment:12435: { id: "Employment:12435", ...},
  // }
  // Allowing faster access via their `id` (no need to loop through the list everytime)
  const mappedAvailableAspects: Record<string, Aspect> = useMemo(
    () => Object.fromEntries(availableAspects.map(aspect => [aspect.id, aspect])),
    [availableAspects],
  )

  // Fallback follows this order:
  // 1. aspect stored in local storage, if assigned to the user
  // 2. first aspect assigned to the user (from the fetch aspects list)
  // 3. first reseller aspect assigned to the user (if any)
  // 4. user has an account but no aspects, so it falls back to the NONE aspect
  const fallbackAspect = useMemo(
    () =>
      mappedAvailableAspects[storedAspectId] ||
      availableAspects[0] ||
      resellerData?.resellerAspects?.collection[0] ||
      FALLBACK_ASPECT,
    [availableAspects, resellerData, mappedAvailableAspects, storedAspectId],
  )

  if (loading || loadingReseller) return null

  return (
    <AccountAspectInitialState
      mappedAvailableAspects={mappedAvailableAspects}
      fallbackAspectId={fallbackAspect.id}
    >
      {currentAspect => (
        <AccountAspectContextProvider
          availableAspects={availableAspects}
          inferredAspect={currentAspect || fallbackAspect}
          {...props}
        />
      )}
    </AccountAspectInitialState>
  )
}
